import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { db } from "@/lib/db";
import { notificationUrl, sanitizeNotificationHtml } from '@/lib/notification-content';
import type { Notification } from "@/types";
import { ActionIcon, Box, Button, ColorSwatch, Divider, Drawer, Group, ScrollArea, Stack, Text, Title, useDrawersStack } from "@mantine/core";
import { IconChevronLeft } from "@tabler/icons-react";
import { useLiveQuery } from "dexie-react-hooks";
import { useEffect, useState } from "react";
import { notificationTable } from "@/components/PQMap/pqMapSlice";
import { fetchNotifications, notificationsLoading } from "./notificationsSlice";

interface NotificationsProps {
    opened: boolean;
    onClose: () => void;
}

const Notifications: React.FC<NotificationsProps> = ({ opened, onClose }) => {
    const stack = useDrawersStack(['notifications', 'notifications-details']);
    const { open: openDrawer, closeAll: closeAllDrawers } = stack;
    const dispatch = useAppDispatch();
    const currentNotificationsTable = useAppSelector(notificationTable)
    const loading = useAppSelector(notificationsLoading)
    const notifications = useLiveQuery(() => db.notifications.orderBy('createdAt').reverse().toArray());
    const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);

    useEffect(() => {
        if (opened) {
            openDrawer('notifications');
        } else {
            closeAllDrawers();
        }
    }, [closeAllDrawers, openDrawer, opened]);

    useEffect(() => {
        if (currentNotificationsTable) {
            dispatch(fetchNotifications(currentNotificationsTable))
        }
    }, [currentNotificationsTable, dispatch]);

    const onSelectedNotification = async (notification: Notification) => {
        if (!notification.viewed) {
            await db.notifications.update(notification.id, { viewed: true });
        }
        setSelectedNotification(notification);
        stack.open('notifications-details');
    }

    const ctaClickHandler = (url: string) => {
        const safeUrl = notificationUrl(url);
        if (safeUrl) window.open(safeUrl, '_blank', 'noopener,noreferrer');
    }
    return (

        <Drawer.Stack>
            <Drawer
                {...stack.register('notifications')}
                position="left"
                withCloseButton={false}
                styles={{ body: { height: '100%' } }}
                size='100%'
                autoFocus={false}
            >
                <Stack style={{ height: '100%' }} gap={5}>
                    <Group align='center' justify='center' style={{ padding: '0' }}>
                        <ActionIcon style={{ position: 'absolute', top: 15, left: 20 }} onClick={onClose} variant='transparent' size='md' aria-label='Close Notifications'>
                            <IconChevronLeft color='black' />
                        </ActionIcon>
                        <Title order={4} mb={10}>Notifications</Title>
                    </Group>
                    <Divider my={0} />
                    <ScrollArea h='100%' py={5}>
                        {/* Notifications List */}
                        {
                            loading && (
                                <Group align='start' justify="center">
                                    <Title order={4}>Loading...</Title>
                                </Group>
                            )
                        }
                        <Stack gap={3} mx="md">
                            {notifications?.map((notification, index) => {
                                return (
                                    <Box key={index}>
                                        <Button variant='transparent' color='black' p={0} styles={{ inner: { justifyContent: 'start' } }}>
                                            <Group align='center' justify="start" my={5}>
                                                {!notification.viewed && <ColorSwatch size={10} color='green'/>}
                                                <Title order={4} onClick={() => onSelectedNotification(notification)}>{notification.title}</Title>
                                            </Group>
                                        </Button>
                                        {
                                            index < notifications.length - 1 && <Divider my={0} />
                                        }
                                    </Box>

                                )
                            })}
                        </Stack>
                    </ScrollArea>
                </Stack>

            </Drawer>
            <Drawer
                {...stack.register('notifications-details')}
                position="left"
                withCloseButton={false}
                styles={{ body: { height: '100%' } }}
                size='100%'
            >
                <Stack style={{ height: '100%' }} gap={5}>
                    <Group align='center' justify='center' style={{ padding: '0' }}>
                        <ActionIcon style={{ position: 'absolute', top: 15, left: 20 }} onClick={() => stack.close('notifications-details')} variant='transparent' size='md' aria-label='Close Notification Detail'>
                            <IconChevronLeft color='black' />
                        </ActionIcon>
                        <Title mb={10} order={4}>Notifications</Title>
                    </Group>
                    <Divider my={0} />
                    <ScrollArea style={{ height: '100%' }}>
                        <Stack justify="center" align="center">
                            <Title c="pq-red" my='sm' order={5}>{selectedNotification?.title}</Title>
                            <Text component="div" dangerouslySetInnerHTML={{ __html: sanitizeNotificationHtml(selectedNotification?.message ?? '') }} />
                            {notificationUrl(selectedNotification?.linkUrl) && <Button color="pq-red" w='80%' my={10} onClick={() => ctaClickHandler(selectedNotification?.linkUrl ?? '')}>Click Here</Button>}
                        </Stack>

                    </ScrollArea>
                </Stack>
            </Drawer>
        </Drawer.Stack>

    );
};

export default Notifications;
